import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';

export default function ReviewForm({ questions, onSubmit, submitting, submitLabel = 'Submit review' }) {
  const [answers, setAnswers] = useState({});
  const ratings = questions.filter(q => q.type === 'rating');
  const texts = questions.filter(q => q.type === 'text');
  const missing = ratings.filter(q => !answers[q.id]).length;

  return (
    <div className="space-y-4" data-testid="review-form">
      {ratings.map(q => (
        <div key={q.id} className="rounded-lg border border-[#E8E2F2] bg-white p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#9B8FAE]">{q.category}</p>
          <p className="text-sm font-semibold text-[#2D1B4E] mt-0.5">{q.text}</p>
          <div className="flex gap-1.5 mt-2">
            {[1, 2, 3, 4, 5].map(v => (
              <button key={v} type="button" onClick={() => setAnswers(a => ({ ...a, [q.id]: v }))}
                      data-testid={`rate-${q.id}-${v}`}
                      className={`h-9 w-9 rounded-full border text-sm font-bold transition-colors ${
                        answers[q.id] === v ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' :
                        (answers[q.id] || 0) >= v ? 'bg-[#F0EAFA] text-[#6B3FA0] border-[#C9B8E8]' :
                        'bg-white text-[#8E85A0] border-[#DED8EB] hover:border-[#6B3FA0]'}`}>
                {v}
              </button>
            ))}
            <span className="text-[10px] text-[#8E85A0] self-center ml-1">
              {answers[q.id] ? ['', 'Needs work', 'Developing', 'Meets expectations', 'Exceeds', 'Outstanding'][answers[q.id]] : '1 = needs work · 5 = outstanding'}
            </span>
          </div>
        </div>
      ))}
      {texts.map(q => (
        <div key={q.id} className="rounded-lg border border-[#E8E2F2] bg-white p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#9B8FAE]">{q.category}</p>
          <p className="text-sm font-semibold text-[#2D1B4E] mt-0.5 mb-2">{q.text}</p>
          <Textarea rows={3} value={answers[q.id] || ''} data-testid={`text-${q.id}`}
                    onChange={e => setAnswers(a => ({ ...a, [q.id]: e.target.value }))}
                    placeholder="Your answer…" />
        </div>
      ))}
      <div className="flex items-center gap-3">
        <Button disabled={submitting || missing > 0} onClick={() => onSubmit(answers)}
                className="bg-[#6B3FA0]" data-testid="review-submit-btn">
          <Star className="h-4 w-4 mr-1" /> {submitLabel}
        </Button>
        {missing > 0 && <span className="text-xs text-[#8E85A0]">{missing} rating{missing > 1 ? 's' : ''} left to answer</span>}
      </div>
    </div>
  );
}

export const StageAnswers = ({ stage, label, review }) => {
  const st = review.stages?.[stage];
  if (!st) return null;
  const qmap = Object.fromEntries(review.questions.map(q => [q.id, q]));
  return (
    <div className="rounded-lg border border-[#E8E2F2] bg-[#FBFAFD] p-3" data-testid={`stage-${stage}`}>
      <p className="text-xs font-black uppercase tracking-wider text-[#6B3FA0]">{label}
        <span className="font-semibold text-[#8E85A0] normal-case tracking-normal ml-2">by {st.submitted_by} · {st.submitted_at?.slice(0, 10)}</span>
      </p>
      <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
        {Object.entries(st.answers).map(([qid, val]) => {
          const q = qmap[qid];
          if (!q) return null;
          return q.type === 'rating' ? (
            <div key={qid} className="flex items-center justify-between rounded bg-white border border-[#EEE9F6] px-2 py-1">
              <span className="text-[11px] text-[#3A3045]">{q.category}</span>
              <span className="text-[11px] font-black text-[#6B3FA0]">{val}/5</span>
            </div>
          ) : (
            <div key={qid} className="sm:col-span-2 rounded bg-white border border-[#EEE9F6] px-2 py-1">
              <span className="text-[10px] font-bold uppercase text-[#9B8FAE]">{q.category}</span>
              <p className="text-[11px] text-[#3A3045] whitespace-pre-wrap">{val}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
